/**
 * The meetings month grid (D1), restyled into a headed card (TMMAPP-0103). My Meetings' Calendar tab
 * shows every virtual account; with `virtualAccountId` it is the virtual account's own Calendar
 * (TMMAPP-0108 folds the copy `MeetingsTab` kept, so one remains). A chip opens the meeting's session.
 * A live meeting is the static `brand` / `brandInk` chip (TMM-T4: no pulse).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingCalendarEntry } from '@/api/meetings';
import { ErrorState } from '@/components/ErrorState';
import { HeadedCard } from '@/components/HeadedCard';
import { MonthCalendar } from '@/components/MonthCalendar';
import { statusLabel } from '@/components/statusLabel';
import { calendarWindow } from '@/features/planned/calendarDates';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { utcToZonedCivil } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { CalendarEntryChip } from './CalendarEntryChip';
import { entryStatus } from './calendarEntry';
import { meetingSessionPath } from './nextMeeting';
import { entryWithLabel } from './standalone';

interface MeetingsCalendarTabProps {
  /** The virtual account whose meetings the grid shows; absent = every meeting of the viewer's. */
  virtualAccountId?: number;
}

export function MeetingsCalendarTab({ virtualAccountId }: MeetingsCalendarTabProps = {}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const navigate = useNavigate();
  const zone = me.user.timezone ?? 'UTC';
  const now = new Date();
  const [month, setMonth] = useState({ year: now.getFullYear(), monthIndex: now.getMonth() });
  const [entries, setEntries] = useState<MeetingCalendarEntry[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const range = calendarWindow(month.year, month.monthIndex);

  const load = useCallback(async () => {
    try {
      const window = { from: range.from, to: range.to };
      const cal =
        virtualAccountId === undefined
          ? await meetingsApi.listMine(window)
          : await meetingsApi.listForVirtualAccount(virtualAccountId, window);
      setEntries(cal.items);
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [range.from, range.to, virtualAccountId]);

  const lastFetch = useRef<string | null>(null);
  useEffect(() => {
    const key = `${virtualAccountId ?? ''}:${range.from}:${range.to}`;
    if (lastFetch.current === key) return;
    lastFetch.current = key;
    void load();
  }, [range.from, range.to, virtualAccountId, load]);

  const virtualAccountName = (id: number) =>
    me.virtualAccounts.find((d) => d.id === id)?.name ?? String(id);

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t(
          virtualAccountId === undefined
            ? 'errors.what.meetings'
            : 'errors.what.virtualAccountMeetings',
        )}
        onRetry={() => void load()}
      />
    );
  }
  if (entries === null) return <Typography>{t('common.loading')}</Typography>;

  return (
    <HeadedCard title={t('meetings.calendar')} subtitle={t('meetings.calendarSub')}>
      <MonthCalendar
        month={month}
        entries={entries.map((entry) => ({
          date: entry.startsAt ? utcToZonedCivil(entry.startsAt, zone).date : range.from,
          key: `${entry.meetingId}-${entry.occurrenceKey}`,
          render: () => (
            <CalendarEntryChip
              entry={entry}
              label={[
                entry.title,
                virtualAccountId === undefined
                  ? entry.virtualAccountId !== null
                    ? virtualAccountName(entry.virtualAccountId)
                    : entryWithLabel(entry, me.user.id, t, locale)
                  : entry.startsAt
                    ? formatInstant(entry.startsAt, locale, zone, { timeStyle: 'short' })
                    : null,
                statusLabel(t, 'meeting', entryStatus(entry)),
              ]
                .filter(Boolean)
                .join(' · ')}
              onClick={() => {
                void navigate(meetingSessionPath(entry));
              }}
            />
          ),
        }))}
        onNavigate={(year, monthIndex) => setMonth({ year, monthIndex })}
        emptyLabel={t('meetings.emptyCalendar')}
      />
    </HeadedCard>
  );
}
